import api from './api';
import type { LegalAudience, LegalConsentItem, LegalDocumentSummary } from '../constants/legalDocuments';

export type LegalDocumentDetail = LegalDocumentSummary & {
  content?: string | null;
};

export type LegalDocumentAdmin = LegalDocumentDetail & {
  id: number;
  isRequired: boolean;
  isCurrent: boolean;
  updatedAt?: string;
  suggestedNextVersion?: string;
};

export type LegalDocumentVersionSummary = {
  id: number;
  slug: string;
  version: string;
  title: string;
  summary?: string | null;
  isCurrent: boolean;
  isRequired: boolean;
  effectiveAt?: string;
  updatedAt?: string;
  hasContent: boolean;
};

class LegalService {
  async getCurrentDocuments(audience?: LegalAudience) {
    const response = await api.get('/legal/documents/current', {
      params: audience ? { audience } : undefined,
    });
    return (response.data?.data || []) as LegalDocumentSummary[];
  }

  async getDocument(slug: string) {
    const response = await api.get(`/legal/documents/${slug}`);
    return response.data?.data as LegalDocumentDetail;
  }

  async getPendingDocuments() {
    const response = await api.get('/legal/pending');
    return {
      audience: response.data?.audience as LegalAudience | null,
      documents: (response.data?.documents || []) as LegalDocumentSummary[],
    };
  }

  async acceptConsents(consents: LegalConsentItem[], context: 'register' | 'login' | 'manual' = 'login') {
    const response = await api.post('/legal/consents/accept', { consents, context });
    return response.data;
  }

  buildConsentPayload(documents: LegalDocumentSummary[]): LegalConsentItem[] {
    return documents.map((doc) => ({ slug: doc.slug, version: doc.version }));
  }

  async listAdminDocuments() {
    const response = await api.get('/legal/admin/documents');
    return (response.data?.data || []) as LegalDocumentAdmin[];
  }

  async updateAdminDocument(
    slug: string,
    payload: {
      title?: string;
      summary?: string | null;
      content?: string | null;
      is_required?: boolean;
    },
  ) {
    const response = await api.put(`/legal/admin/documents/${slug}`, payload);
    return response.data?.data as LegalDocumentAdmin;
  }

  async listAdminDocumentVersions(slug: string) {
    const response = await api.get(`/legal/admin/documents/${slug}/versions`);
    return (response.data?.data || []) as LegalDocumentVersionSummary[];
  }

  async publishAdminDocument(
    slug: string,
    payload: {
      version: string;
      title?: string;
      summary?: string | null;
      content?: string | null;
      is_required?: boolean;
    },
  ) {
    const response = await api.post(`/legal/admin/documents/${slug}/publish`, payload);
    return {
      data: response.data?.data as LegalDocumentAdmin,
      message: response.data?.message as string | undefined,
    };
  }
}

const legalService = new LegalService();
export default legalService;
